import { HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { HttpHeaderConstants } from '@r1-compass-library/constants';
import { Environment } from '@r1-compass-library/environments/environment';
import { BasicAuthCredentials, UsersIamDivisionChoice } from '@r1-compass-library/models';
import { LocalStorageService } from '@r1-compass-library/services/local-storage.service';
import { LoginCookieService } from '@r1-compass-library/services/login-cookie.service';
import { SpinnerService } from '@r1-compass-library/services/spinner.service';
import { catchError, filter, finalize, Observable, tap, throwError } from 'rxjs';

type IamUserDivisionInfo = {
    divs: UsersIamDivisionChoice[];
    divToken: string;
};

export const TokenTypes = { Jwt: 'jwt-access', Div: 'divToken' } as const;
export type TokenType = (typeof TokenTypes)[keyof typeof TokenTypes];

@Injectable({ providedIn: 'root' })
export class LoginService {
    readonly #loginCookieService = inject(LoginCookieService);
    readonly #localStorageService = inject(LocalStorageService);
    readonly #spinnerService = inject(SpinnerService);
    readonly #http = inject(HttpClient);

    private BASE_URL = Environment.baseUrl;

    /**
     * @description Sets division token and divisions cookie, as well as setting a user's pickable divisions
     * @param userInfo
     */
    login(userInfo: BasicAuthCredentials) {
        const httpOptions = {
            headers: new HttpHeaders({
                ...HttpHeaderConstants.defaultHeaders,
                ...{ Authorization: btoa(`${userInfo.username}:${userInfo.password}`) },
            }),
        };
        const process = 'LoginService.login';
        this.#spinnerService.addProcess(process, 'Logging in...');
        return this.#http.get<IamUserDivisionInfo>(`${this.BASE_URL}/signin`, httpOptions).pipe(
            filter(res => !!res.divToken),
            tap(res => this.storeToken(res.divToken, TokenTypes.Div)),
            catchError(this.handleError),
            finalize(() => this.#spinnerService.completeProcess(process))
        );
    }

    selectDivision(divisionCode: string) {
        const httpOptions = {
            headers: new HttpHeaders({
                ...HttpHeaderConstants.defaultHeaders,
                ...{
                    divtoken: this.getToken(TokenTypes.Div),
                },
                ...{
                    activediv: divisionCode,
                },
            }),
        };
        const process = 'LoginService.selectDivision';
        this.#spinnerService.addProcess(process, 'Selecting division...');
        return this.#http
            .get<{ jwtToken: string }>(`${this.BASE_URL}/validateDivToken?division=${divisionCode}`, httpOptions)
            .pipe(
                filter(res => !!res.jwtToken),
                tap(res => this.storeToken(res.jwtToken, TokenTypes.Jwt)),
                catchError(this.handleError),
                finalize(() => this.#spinnerService.completeProcess(process))
            );
    }

    getToken(tokenType: TokenType = TokenTypes.Jwt) {
        const headerAndClaims = this.#localStorageService.getItem(tokenType);
        if (tokenType === TokenTypes.Div) {
            return headerAndClaims + '.' + this.#loginCookieService.getDivTokenCookie();
        } else {
            return headerAndClaims + '.' + this.#loginCookieService.getJWTCookie();
        }
    }

    private storeToken(token: string, tokenType: TokenType) {
        const [header, claims, signature] = token.split('.');
        this.#localStorageService.setItem(tokenType, header + '.' + claims);
        if (tokenType === TokenTypes.Div) {
            this.#loginCookieService.setDivTokenCookie(signature);
        } else if (tokenType === TokenTypes.Jwt) {
            this.#loginCookieService.setJWTCookie(signature);
        }
    }

    private handleError(err: HttpErrorResponse): Observable<never> {
        return throwError(() => err);
    }
}
